/**
 * PostHog analytics — frontend singleton.
 *
 * Initializes posthog-js with autocapture, session replay, and web vitals.
 * Disabled in development mode to avoid polluting analytics with dev traffic.
 *
 * Usage:
 *   import { posthog } from '@/lib/posthog';
 *   posthog.capture('manual_upload_started', { filename: 'manual.pdf' });
 *
 * Env vars (VITE_ prefix = bundled into frontend):
 *   VITE_POSTHOG_KEY — project API key (phc_...)
 *   VITE_POSTHOG_HOST — ingestion endpoint (https://us.i.posthog.com)
 */

import posthogJs from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://us.i.posthog.com';

/**
 * Initialize PostHog. Safe to call multiple times (idempotent).
 * Returns the posthog instance for immediate use.
 */
export function initPostHog(): typeof posthogJs {
  if (!POSTHOG_KEY) {
    // No key configured — return the uninitialized instance (all calls become no-ops).
    return posthogJs;
  }

  // Already initialized check (posthog-js sets __loaded internally).
  if ((posthogJs as any).__loaded) {
    return posthogJs;
  }

  posthogJs.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,

    // ── Autocapture ───────────────────────────────────────────
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,

    // ── Session Replay ────────────────────────────────────────
    enable_recording_console_log: true,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },

    // ── Performance ───────────────────────────────────────────
    capture_performance: true,

    // ── Privacy ───────────────────────────────────────────────
    persistence: 'localStorage',

    // ── Dev mode: opt out to avoid polluting data ─────────────
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        ph.opt_out_capturing();
      }
    },
  });

  return posthogJs;
}

/**
 * The PostHog instance. Import this anywhere in the frontend to track events.
 * If PostHog is not configured (no VITE_POSTHOG_KEY), all calls are safe no-ops.
 */
export const posthog = posthogJs;
